package com.isaias.fyattravel.web.components

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import org.jetbrains.compose.web.attributes.InputType
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

private const val HOTEL_WHATSAPP_NUMBER = "256758250625"
private val EMAIL_REGEX = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")

/**
 * Hotel inquiry card component. Collects stay details and emails
 * info@fyattraveltour.com instead of performing a live search.
 */
@Composable
fun HotelSearchCard() {
    var cityQuery by remember { mutableStateOf("London") }
    var arrivalDate by remember { mutableStateOf("2026-12-13") }
    var departureDate by remember { mutableStateOf("2026-12-16") }
    var adults by remember { mutableStateOf("2") }
    var rooms by remember { mutableStateOf("1") }
    var email by remember { mutableStateOf("") }

    var sending by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var attemptedSend by remember { mutableStateOf(false) }
    val locale = WebLocaleStore.locale.value
    fun t(key: WebStringKey) = WebStrings.get(locale, key)

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px ${ThemeColors.shadow}")
            marginBottom(2.em)
        }
    }) {
        Div({
            classes(ResponsiveStyles.hotelSearchGrid)
            style {
                display(DisplayStyle.Grid)
                gap(1.em)
                alignItems(AlignItems.End)
            }
        }) {
            TextInputBox(t(WebStringKey.HOTEL_SEARCH_DESTINATION), cityQuery) { cityQuery = it }
            DateInputBox(t(WebStringKey.HOTEL_CHECK_IN), arrivalDate) { arrivalDate = it }
            DateInputBox(t(WebStringKey.HOTEL_CHECK_OUT), departureDate) { departureDate = it }
            TextInputBox(t(WebStringKey.HOTEL_ADULTS), adults) { adults = it }
            TextInputBox(t(WebStringKey.HOTEL_ROOMS), rooms) { rooms = it }
            EmailInputBox(t(WebStringKey.EMAIL_LABEL), email, attemptedSend && !EMAIL_REGEX.matches(email.trim())) { email = it }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(0.75.em)
                marginTop(1.em)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button({
                style {
                    backgroundColor(if (sending) ThemeColors.primaryMuted else ThemeColors.primary)
                    color(ThemeColors.textPrimary)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor(if (sending) "not-allowed" else "pointer")
                    fontWeight("700")
                    property("box-shadow", "0 4px 12px ${ThemeColors.shadow}")
                }
                onClick {
                    attemptedSend = true
                    successMessage = null
                    if (sending) return@onClick
                    if (!EMAIL_REGEX.matches(email.trim())) {
                        error = t(WebStringKey.VALIDATION_ENTER_VALID_EMAIL)
                        return@onClick
                    }
                    sending = true
                    error = null

                    submitInquiry(
                        type = "stay",
                        email = email.trim(),
                        fields = linkedMapOf(
                            t(WebStringKey.HOTEL_SEARCH_DESTINATION) to cityQuery.trim(),
                            t(WebStringKey.HOTEL_CHECK_IN) to arrivalDate.trim(),
                            t(WebStringKey.HOTEL_CHECK_OUT) to departureDate.trim(),
                            t(WebStringKey.HOTEL_ADULTS) to adults.trim(),
                            t(WebStringKey.HOTEL_ROOMS) to rooms.trim(),
                        )
                    ) { ok, message ->
                        sending = false
                        if (ok) {
                            successMessage = t(WebStringKey.ENQUIRY_SENT)
                            attemptedSend = false
                        } else {
                            error = message ?: t(WebStringKey.ERROR_NETWORK)
                        }
                    }
                }
            }) { Text(if (sending) t(WebStringKey.PLEASE_WAIT) else t(WebStringKey.SEND_EMAIL)) }

            Button({
                style {
                    backgroundColor(ThemeColors.whatsapp)
                    color(ThemeColors.textPrimary)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                }
                onClick {
                    val message = buildString {
                        appendLine(t(WebStringKey.EXPLORE_STAYS))
                        appendLine("${t(WebStringKey.HOTEL_SEARCH_DESTINATION)}: ${cityQuery.trim()}")
                        appendLine("${t(WebStringKey.HOTEL_CHECK_IN)}: ${arrivalDate.trim()}")
                        appendLine("${t(WebStringKey.HOTEL_CHECK_OUT)}: ${departureDate.trim()}")
                        appendLine("${t(WebStringKey.HOTEL_ADULTS)}: ${adults.trim()}")
                        appendLine("${t(WebStringKey.HOTEL_ROOMS)}: ${rooms.trim()}")
                        if (email.isNotBlank()) appendLine("${t(WebStringKey.EMAIL_LABEL)}: ${email.trim()}")
                    }
                    val encoded = js("encodeURIComponent")(message) as String
                    window.open("https://wa.me/$HOTEL_WHATSAPP_NUMBER?text=$encoded", "_blank")
                }
            }) { Text(t(WebStringKey.WHATSAPP)) }
        }

        error?.let { msg ->
            Br()
            P({
                style {
                    color(ThemeColors.error)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }

        successMessage?.let { msg ->
            Br()
            P({
                style {
                    color(ThemeColors.success)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }
    }
}

@Composable
private fun TextInputBox(label: String, value: String, onValueChange: (String) -> Unit) {
    Div {
        Label(null, {
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                display(DisplayStyle.Block)
            }
        }) { Text(label) }
        Input(type = InputType.Text, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.borderLight}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
            }
        })
    }
}

@Composable
private fun DateInputBox(label: String, value: String, onValueChange: (String) -> Unit) {
    Div {
        Label(null, {
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                display(DisplayStyle.Block)
            }
        }) { Text(label) }
        Input(type = InputType.Date, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.borderLight}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
                fontSize(0.95.em)
            }
        })
    }
}
